import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { HudIconButtonComponent } from '@features/hud/hud-icon-button/hud-icon-button.component';
import { LpInventoryItemContentComponent } from '@features/lp/lp-inventory/lp-inventory-item-content/lp-inventory-item-content.component';
import { LpInventoryItemPreviewComponent } from '@features/lp/lp-inventory/lp-inventory-item-preview/lp-inventory-item-preview.component';
import { MODAL_ANIMATION } from '@features/styles/animations';
import { StyleSheetSpriteComponent } from '@features/styles/style-sheet-sprite/style-sheet-sprite.component';
import { StylesService } from '@features/styles/styles.service';
import { EASE_OUT_BACK } from '@shared/css.utils';
import { LpItem, LpItemGroup } from '@typings/lp/generated';

@Component({
  selector: 'lp-inventory',
  imports: [StyleSheetSpriteComponent, LpInventoryItemContentComponent, LpInventoryItemPreviewComponent, HudIconButtonComponent],
  templateUrl: './lp-inventory.component.html',
  styleUrl: './lp-inventory.component.css',
  animations: [
    MODAL_ANIMATION(true),
    trigger('stagger', [
      transition('* => *, :enter', [
        query(
          ':enter',
          [style({ scale: 0.75, opacity: 0 }), stagger(50, [animate(`0.25s ${EASE_OUT_BACK}`, style({ opacity: 1, scale: 1 }))])],
          { optional: true },
        ),
      ]),
    ]),
  ],
})
export class LpInventoryComponent implements OnInit {
  readonly _stylesService = inject(StylesService);

  items = input.required<LpItem[]>();
  enableDebug = input<boolean>();
  disableTabs = input<boolean>(false);

  removeItem = output<number>();

  tabs = computed(() => {
    const tabs: { group: LpItemGroup; items: (LpItem & { index: number; url: string })[] }[] = [];
    let index = -1;
    for (const item of this.items()) {
      index++;

      if (!item.group) continue;
      if (item.icon.url.length <= 0) continue;

      let tab = tabs.find((tab) => tab.group.uid === item.group!.uid);
      if (!tab) {
        tab = { group: item.group!, items: [] };
        tabs.push(tab);
      }

      tab.items.push({ ...item, index, url: item.icon.url });
    }
    return tabs;
  });

  selectedTabIndex = signal(-1);
  selectedTab = computed(() => (this.selectedTabIndex() !== -1 ? this.tabs()?.[this.selectedTabIndex()] : undefined));
  selectedItemIndex = signal(-1);
  selectedItem = computed(() => (this.selectedItemIndex() !== -1 ? this.selectedTab()?.items[this.selectedItemIndex()] : undefined));

  ngOnInit() {
    // display the stagger animation on the first load
    setTimeout(() => {
      this.selectedTabIndex.set(0);
    }, 100); // NOTE: we wait 100ms so that the inventory opening animation has some time to play
  }

  selectTab(index: number) {
    this.selectedTabIndex.set(-1);
    this.selectedItemIndex.set(-1);
    setTimeout(() => {
      this.selectedTabIndex.set(index);
    }, 0); // NOTE: by first setting it to -1, then waiting a tick, and then setting the correct index, the stagger animation is re-triggered
  }
}
